import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line,
} from "recharts";
import {
  Building2, MapPin, Plus, Check, X, TrendingUp, Search, ArrowLeft, Database,
  Scale, HelpCircle, Download, FileText, Lightbulb, ChevronRight, ChevronLeft,
} from "lucide-react";
import { EqualMark, Eyebrow, AppHeader, StepNumber } from "@/components/brand";
import { Learn } from "@/components/Learn";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TUTORIAL_KEY = "equal_comps_tutorial_v1";

const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
const sizeRange = (c) => {
  if (c.sqft_min && c.sqft_max) return `${c.sqft_min.toLocaleString()}–${c.sqft_max.toLocaleString()} sf`;
  if (c.sqft_min) return `${c.sqft_min.toLocaleString()}+ sf`;
  if (c.sqft_max) return `up to ${c.sqft_max.toLocaleString()} sf`;
  return "—";
};

const inputCls = "input-flat";
const labelCls = "block text-xs font-medium text-mute mb-1";

const STATUS_STYLE = {
  operating: { dot: "#17a35f", cls: "bg-mint text-equal-dark border-[#bfe5d1]" },
  in_buildout: { dot: "#1f5f8b", cls: "bg-[#e8eef7] text-[#1f5f8b] border-[#c9d8ea]" },
  proposed: { dot: "#5b4bb5", cls: "bg-[#eeebfa] text-[#5b4bb5] border-[#d6d0f0]" },
  closed: { dot: "#c0392b", cls: "bg-[#fdecea] text-[#c0392b] border-[#f2c1bb]" },
  never_opened: { dot: "#1c2b3c", cls: "bg-line text-ink-2 border-[#d3d9df]" },
  unknown: { dot: "#9aa6b2", cls: "bg-page text-mute border-line" },
};

const TABS = [
  { id: "browse", label: "Browse Comps", icon: Database },
  { id: "benchmarks", label: "Benchmarks & Trends", icon: TrendingUp },
  { id: "submit", label: "Submit a Comp", icon: Plus },
];

/* Glossary terms reused in tutorial + tooltips */
const GLOSSARY = [
  { term: "$/sf per month", def: "Base rent quoted per square foot, charged every month. In Southern California, rents are quoted monthly (a 1,500 sf space at $3.50/sf = $5,250/mo)." },
  { term: "Triple Net (NNN)", def: "You pay base rent PLUS property taxes, insurance, and common-area maintenance. The headline rent is lower, but your real cost is higher — always ask for the NNN estimate." },
  { term: "Modified Gross", def: "Rent includes some operating costs (often taxes/insurance) but you pay your own utilities/janitorial. A middle ground between NNN and full gross." },
  { term: "Gross / Full Service", def: "One flat rent covers most costs (taxes, insurance, maintenance). Simplest to budget, usually the highest headline rate." },
  { term: "TI allowance", def: "Tenant Improvement allowance — money the landlord contributes toward building out your space. Higher TI lowers your out-of-pocket buildout cost." },
  { term: "Free rent", def: "Months of rent the landlord waives at move-in (often during buildout). Reduces your effective rent over the lease." },
  { term: "Effective rent", def: "Your TRUE average cost after factoring in free rent and TI — not just the headline number. Two deals at the same base rent can cost very differently." },
];

const CHART_TICK = { fontSize: 11, fill: "#5b6b7c", fontFamily: "'JetBrains Mono', monospace" };
const CHART_TOOLTIP = { borderRadius: 10, border: "1px solid #e9ebed", fontSize: 12, boxShadow: "none" };

export default function CompDatabasePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email");
  const role = localStorage.getItem("role");
  const isAdmin = role === "admin";

  const [tab, setTab] = useState("browse");
  const [meta, setMeta] = useState({ asset_classes: [], lease_types: [], statuses: [], status_labels: {} });
  const [comps, setComps] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [pending, setPending] = useState([]);
  const [filters, setFilters] = useState({ asset_class: "", zip_code: "", lease_type: "", operational_status: "" });
  const [showTutorial, setShowTutorial] = useState(false);

  const cfg = useCallback(() => {
    const c = { withCredentials: true };
    if (token) c.headers = { Authorization: `Bearer ${token}` };
    return c;
  }, [token]);

  const homePath = isAdmin ? "/admin" : role === "city_official" ? "/city" : "/home";

  const loadComps = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.asset_class) params.set("asset_class", filters.asset_class);
      if (filters.zip_code) params.set("zip_code", filters.zip_code.trim());
      if (filters.lease_type) params.set("lease_type", filters.lease_type);
      if (filters.operational_status) params.set("operational_status", filters.operational_status);
      const res = await axios.get(`${API}/comps?${params.toString()}`, cfg());
      setComps(res.data.comps || []);
    } catch { toast.error("Failed to load comparables"); }
  }, [filters, cfg]);

  const loadBenchmarks = useCallback(async () => {
    try { setBenchmarks((await axios.get(`${API}/comps/benchmarks`, cfg())).data); } catch { /* optional */ }
  }, [cfg]);

  const loadPending = useCallback(async () => {
    if (!isAdmin) return;
    try { setPending((await axios.get(`${API}/comps?status=pending`, cfg())).data.comps || []); } catch { /* ignore */ }
  }, [isAdmin, cfg]);

  useEffect(() => {
    axios.get(`${API}/comps/meta`, cfg()).then((r) => setMeta(r.data)).catch(() => {});
    loadBenchmarks();
    loadPending();
    if (!localStorage.getItem(TUTORIAL_KEY)) setTimeout(() => setShowTutorial(true), 500);
  }, [cfg, loadBenchmarks, loadPending]);

  useEffect(() => { loadComps(); }, [loadComps]);

  const refreshAll = () => { loadComps(); loadBenchmarks(); loadPending(); };
  const dismissTutorial = () => { setShowTutorial(false); localStorage.setItem(TUTORIAL_KEY, "1"); };

  const allTabs = isAdmin
    ? [...TABS, { id: "review", label: "Review Queue", icon: Check, badge: pending.length }]
    : TABS;

  return (
    <div className="min-h-screen bg-page" data-testid="comp-database-page">
      <AppHeader section="Market Data">
        <button onClick={() => setShowTutorial(true)} data-testid="comp-help-button"
          className="btn-ghost btn-sm text-equal-dark">
          <HelpCircle className="w-3.5 h-3.5" /> How to read this
        </button>
        <button onClick={() => navigate(homePath)} data-testid="comp-back-button"
          className="btn-ghost btn-sm">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <span className="text-mute text-xs font-mono hidden sm:inline">{email}</span>
      </AppHeader>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 animate-rise-in">
        <div className="mb-10">
          <Eyebrow className="text-equal-dark mb-3">Comparables Database</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">
            Know what the market really costs.
          </h1>
          <p className="text-mute mt-4 text-sm md:text-base max-w-2xl leading-relaxed">
            Real, anonymized lease &amp; sale comparables from across the equity program — so you walk into negotiations with leverage. New to this?{" "}
            <button onClick={() => setShowTutorial(true)} className="text-equal-dark underline underline-offset-2 hover:text-ink">Take the quick tour.</button>
          </p>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-line mb-8">
          {allTabs.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`comp-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? "border-equal text-ink" : "border-transparent text-mute hover:text-ink"}`}>
                <Icon className={`w-4 h-4 ${active ? "text-equal" : ""}`} /> {t.label}
                {t.badge > 0 && <span className="ml-1 font-mono text-[10px] font-semibold bg-warm text-[#c47a12] border border-[#f3d9b0] rounded-md px-1.5 py-0.5">{t.badge}</span>}
              </button>
            );
          })}
        </div>

        {tab === "browse" && <BrowseTab comps={comps} filters={filters} setFilters={setFilters} meta={meta} benchmarks={benchmarks} isAdmin={isAdmin} onChanged={refreshAll} cfg={cfg} />}
        {tab === "benchmarks" && <BenchmarksTab benchmarks={benchmarks} meta={meta} />}
        {tab === "submit" && <SubmitTab meta={meta} isAdmin={isAdmin} cfg={cfg} onSubmitted={refreshAll} />}
        {tab === "review" && isAdmin && <ReviewTab pending={pending} cfg={cfg} onChanged={refreshAll} meta={meta} />}
      </main>

      {showTutorial && <TutorialModal onClose={dismissTutorial} />}
    </div>
  );
}

/* ───────────────────────── Glossary tooltip ───────────────────────── */
const Term = ({ label, def }) => (
  <span className="relative group inline-flex items-center">
    <span className="border-b border-dotted border-mute cursor-help">{label}</span>
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-[10px] bg-ink text-white text-xs p-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-30 leading-relaxed normal-case font-normal tracking-normal">
      {def}
    </span>
  </span>
);

const StatusBadge = ({ status, labels }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  const label = (labels && labels[status]) || "Unknown";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-md border ${s.cls}`} data-testid={`comp-status-badge-${status}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} /> {label}
    </span>
  );
};

/* ───────────────────────── Browse ───────────────────────── */
function BrowseTab({ comps, filters, setFilters, meta, benchmarks, isAdmin, onChanged, cfg }) {
  const ov = benchmarks?.overall;

  const deleteComp = async (id) => {
    try { await axios.delete(`${API}/comps/${id}`, cfg()); toast.success("Comp deleted"); onChanged(); }
    catch { toast.error("Failed to delete"); }
  };

  const filterParams = () => {
    const p = new URLSearchParams();
    if (filters.asset_class) p.set("asset_class", filters.asset_class);
    if (filters.zip_code) p.set("zip_code", filters.zip_code.trim());
    if (filters.lease_type) p.set("lease_type", filters.lease_type);
    if (filters.operational_status) p.set("operational_status", filters.operational_status);
    return p;
  };

  // Open a server URL in a real new tab — runs outside the sandboxed preview iframe,
  // so cookie-authenticated downloads (CSV) and inline views (PDF) work reliably.
  const openInNewTab = (url) => {
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const exportCSV = () => {
    if (comps.length === 0) { toast.error("Nothing to export"); return; }
    openInNewTab(`${API}/comps/export/csv?${filterParams().toString()}`);
    toast.success("Downloading CSV…");
  };

  const exportPDF = () => {
    if (comps.length === 0) { toast.error("Nothing to export"); return; }
    openInNewTab(`${API}/comps/export?${filterParams().toString()}`);
    toast.success("Opening Market Snapshot…");
  };

  return (
    <div className="space-y-6" data-testid="comp-browse-tab">
      {ov && ov.count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Comps on file" value={benchmarks.total_comps} />
          <Stat label="Median base rent" value={`${money(ov.median)}/sf`} accent />
          <Stat label="Market range" value={`${money(ov.min)}–${money(ov.max)}`} />
          <Stat label="Still operating" value={`${benchmarks.status_breakdown?.operating ?? 0} of ${benchmarks.total_comps}`} />
        </div>
      )}

      {/* Filters + export */}
      <div className="card-flat flex flex-wrap items-end gap-3 p-4">
        <div className="flex-1 min-w-[150px]">
          <label className={labelCls}>Asset class</label>
          <select className={inputCls} value={filters.asset_class} data-testid="comp-filter-asset-class" onChange={(e) => setFilters({ ...filters, asset_class: e.target.value })}>
            <option value="">All asset classes</option>
            {meta.asset_classes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="w-28">
          <label className={labelCls}>ZIP code</label>
          <input className={inputCls} placeholder="90802" value={filters.zip_code} data-testid="comp-filter-zip" onChange={(e) => setFilters({ ...filters, zip_code: e.target.value })} />
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className={labelCls}>Lease type</label>
          <select className={inputCls} value={filters.lease_type} data-testid="comp-filter-lease-type" onChange={(e) => setFilters({ ...filters, lease_type: e.target.value })}>
            <option value="">All lease types</option>
            {meta.lease_types.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className={labelCls}>Outcome</label>
          <select className={inputCls} value={filters.operational_status} data-testid="comp-filter-status" onChange={(e) => setFilters({ ...filters, operational_status: e.target.value })}>
            <option value="">Any outcome</option>
            {(meta.statuses || []).map((s) => <option key={s} value={s}>{meta.status_labels?.[s] || s}</option>)}
          </select>
        </div>
        {(filters.asset_class || filters.zip_code || filters.lease_type || filters.operational_status) && (
          <button onClick={() => setFilters({ asset_class: "", zip_code: "", lease_type: "", operational_status: "" })} data-testid="comp-clear-filters"
            className="btn-ghost">Clear</button>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-mute">{comps.length} comparable{comps.length === 1 ? "" : "s"}</p>
        <div className="flex gap-2">
          <button onClick={exportCSV} data-testid="comp-export-csv"
            className="btn-ghost h-9 px-3.5">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={exportPDF} data-testid="comp-export-pdf"
            className="btn-primary h-9 px-3.5 disabled:opacity-60">
            <FileText className="w-4 h-4" /> Market Snapshot (PDF)
          </button>
        </div>
      </div>

      {comps.length === 0 ? (
        <EmptyState icon={Search} title="No comparables match" body="Try clearing filters, or be the first to contribute a comp for this segment." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="comp-table">
            <thead>
              <tr className="text-left">
                {["Asset class", "ZIP", "Size", "Base rent", "Lease type", "Lease length", "Signed", "Outcome", "TI / Free rent"].map((h) => (
                  <th key={h} className="th-mono px-4 py-3 border-b border-line">{h}</th>
                ))}
                {isAdmin && <th className="px-4 py-3 border-b border-line"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {comps.map((c) => (
                <tr key={c.id} className="hover:bg-page transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-semibold text-ink">{c.asset_class}</span>
                    {c.property_type && <span className="block text-xs text-mute">{c.property_type}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-2 font-mono text-xs">{c.zip_code}</td>
                  <td className="px-4 py-3 text-ink-2">{sizeRange(c)}</td>
                  <td className="px-4 py-3 font-semibold text-equal-dark tabular-nums">{c.base_rent_psf ? `${money(c.base_rent_psf)}/sf` : "—"}</td>
                  <td className="px-4 py-3 text-ink-2">{c.lease_type || "—"}</td>
                  <td className="px-4 py-3 text-ink font-medium">{c.lease_term || "—"}</td>
                  <td className="px-4 py-3 text-mute text-xs font-mono">{c.deal_date || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.operational_status || "unknown"} labels={meta.status_labels} /></td>
                  <td className="px-4 py-3 text-ink-2 text-xs">
                    {c.ti_allowance ? `TI ${c.ti_allowance}` : ""}{c.ti_allowance && c.free_rent ? " · " : ""}{c.free_rent ? `${c.free_rent} free` : ""}{!c.ti_allowance && !c.free_rent ? "—" : ""}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteComp(c.id)} data-testid={`comp-delete-${c.id}`} className="text-mute/60 hover:text-[#c0392b] transition-colors"><X className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Key terms legend */}
      <div className="rounded-xl bg-cream border border-[#e6dfd0] p-4">
        <Eyebrow className="mb-2">Key terms</Eyebrow>
        <p className="text-sm text-ink-2 leading-relaxed">
          Hover any of these for a plain-English definition:{" "}
          {GLOSSARY.slice(0, 6).map((g, i) => (
            <span key={g.term}>{i > 0 ? " · " : ""}<Term label={g.term} def={g.def} /></span>
          ))}
        </p>
      </div>

      <p className="text-xs text-mute flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" /> For privacy, comps show ZIP code and a size range only — never a street address or party names.
      </p>
    </div>
  );
}

/* ───────────────────────── Benchmarks ───────────────────────── */
function BenchmarksTab({ benchmarks, meta }) {
  const [cmpClass, setCmpClass] = useState("");
  const [cmpRent, setCmpRent] = useState("");

  if (!benchmarks || benchmarks.priced_comps === 0) {
    return <EmptyState icon={TrendingUp} title="Not enough priced comps yet" body="Once a few comparables with base rent are approved, benchmarks and trend charts will appear here." />;
  }

  const barData = benchmarks.by_asset_class.filter((r) => r.count > 0).map((r) => ({ name: r.asset_class, median: r.median }));
  const trendData = benchmarks.trend;
  const sb = benchmarks.status_breakdown || {};
  const ov = benchmarks.overall;

  const selected = benchmarks.by_asset_class.find((r) => r.asset_class === cmpClass);
  const rentNum = parseFloat(cmpRent);
  let verdict = null;
  if (selected && selected.count > 0 && !isNaN(rentNum)) {
    let pct = selected.max > selected.min ? ((rentNum - selected.min) / (selected.max - selected.min)) * 100 : (rentNum <= selected.median ? 0 : 100);
    pct = Math.max(0, Math.min(100, pct));
    let tone = "good"; let text = "";
    if (rentNum <= selected.median) { tone = "good"; text = `At or below the market median of ${money(selected.median)}/sf — a strong position. Confirm what's included (NNN vs gross) before celebrating.`; }
    else if (rentNum <= selected.max) { tone = "warn"; text = `Above the median (${money(selected.median)}/sf) but within the market range. There's room to negotiate down toward the median.`; }
    else { tone = "high"; text = `Above the highest comp (${money(selected.max)}/sf) for this class. Push back hard — bring these benchmarks to the table.`; }
    verdict = { pct, tone, text };
  }

  // Plain-language insight
  const operating = sb.operating || 0; const closed = sb.closed || 0; const never = sb.never_opened || 0;
  const proposed = sb.proposed || 0;
  const executed = Math.max(0, benchmarks.total_comps - proposed);
  const survival = executed ? Math.round((operating / executed) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="comp-benchmarks-tab">
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total comps" value={benchmarks.total_comps} />
          <Stat label="Overall median" value={`${money(ov.median)}/sf`} accent />
          <Stat label="Overall range" value={`${money(ov.min)}–${money(ov.max)}`} />
          <Stat label="Avg base rent" value={`${money(ov.avg)}/sf`} />
        </div>
        <p className="mt-3 text-xs text-mute">
          Benchmarks reflect <b className="text-ink-2">executed leases only</b> ({ov.count} priced){proposed > 0 ? <> — {proposed} proposed/asking term sheet{proposed === 1 ? "" : "s"} excluded</> : null}.
        </p>
      </div>

      {/* What this means for you */}
      <div className="rounded-xl border border-[#bfe5d1] bg-mint p-5" data-testid="comp-insight-card">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-4 h-4 text-equal-dark" />
          <p className="text-sm font-bold text-ink tracking-tight">What this means for you</p>
        </div>
        <p className="text-sm text-ink-2 leading-relaxed">
          Base rents run <b>{money(ov.min)}–{money(ov.max)}/sf per month</b>; the median <b>{money(ov.median)}/sf</b> is your negotiating anchor.
        </p>
        {(closed > 0 || never > 0) && (
          <p className="text-sm text-ink-2 leading-relaxed mt-2">
            <b>{operating} of {executed}</b> executed tenants are still operating ({survival}%); <b>{closed}</b> closed{never > 0 ? <>, <b>{never}</b> never opened</> : null}.
          </p>
        )}
        <Learn className="mt-3" testId="comp-insight-more">
          Anything well above the range is above market and worth pushing back on. A cheap rent is <i>not</i> automatically a good deal — if a tenant couldn't survive under those terms, treat it as a warning, not a target.
          {proposed > 0 && <> <b>{proposed}</b> proposed / asking term sheet{proposed === 1 ? "" : "s"} (not signed deals) show what landlords are offering but are kept out of the medians above.</>}
        </Learn>
      </div>

      {/* Outcome breakdown */}
      <div>
        <Eyebrow className="mb-3">Tenant outcomes at these terms</Eyebrow>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="comp-outcome-breakdown">
          {["operating", "in_buildout", "proposed", "closed", "never_opened", "unknown"].map((s) => (
            <div key={s} className="card-flat p-3 flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_STYLE[s].dot }} />
              <div>
                <p className="text-lg font-bold text-ink tracking-tight tabular-nums">{sb[s] || 0}</p>
                <p className="text-xs text-mute">{meta.status_labels?.[s] || s}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compare your deal */}
      <div className="card-flat p-5">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-equal-dark" />
          <p className="text-sm font-bold text-ink tracking-tight">How does your deal compare?</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className={labelCls}>Asset class</label>
            <select className={inputCls} value={cmpClass} data-testid="comp-compare-class" onChange={(e) => setCmpClass(e.target.value)}>
              <option value="">Select…</option>
              {meta.asset_classes.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="w-44">
            <label className={labelCls}>Your base rent ($/sf/mo)</label>
            <input className={inputCls} type="number" step="0.01" placeholder="3.25" value={cmpRent} data-testid="comp-compare-rent" onChange={(e) => setCmpRent(e.target.value)} />
          </div>
        </div>
        {cmpClass && (!selected || selected.count === 0) && <p className="text-xs text-mute mt-3">No priced comps for {cmpClass} yet — add some to unlock comparison.</p>}
        {verdict && (
          <div className="mt-4" data-testid="comp-compare-result">
            <div className="relative h-3 rounded-full bg-gradient-to-r from-[#bfe5d1] via-[#f3d9b0] to-[#f2c1bb]">
              <div className="absolute -top-1.5 w-1 h-6 bg-ink rounded" style={{ left: `calc(${verdict.pct}% - 2px)` }} />
            </div>
            <div className="flex justify-between text-[10px] text-mute mt-1 font-mono">
              <span>{money(selected.min)} low</span><span>{money(selected.median)} median</span><span>{money(selected.max)} high</span>
            </div>
            <p className={`text-sm mt-3 font-medium ${verdict.tone === "good" ? "text-equal-dark" : verdict.tone === "warn" ? "text-[#c47a12]" : "text-[#c0392b]"}`}>{verdict.text}</p>
            <p className="text-xs text-mute mt-2">Tip: compare <Term label="effective rent" def={GLOSSARY.find((g) => g.term === "Effective rent").def} />, not the headline number.</p>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="card-flat p-5">
        <Eyebrow className="mb-4">Median base rent by asset class ($/sf/mo)</Eyebrow>
        <div className="h-64 w-full" data-testid="comp-bar-chart">
          <ResponsiveContainer width="100%" height={256}>
            <BarChart data={barData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ebed" vertical={false} />
              <XAxis dataKey="name" tick={CHART_TICK} interval={0} angle={-12} textAnchor="end" height={50} axisLine={{ stroke: "#e9ebed" }} tickLine={false} />
              <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [`$${v}/sf`, "Median"]} contentStyle={CHART_TOOLTIP} cursor={{ fill: "#f5f6f4" }} />
              <Bar dataKey="median" fill="#17a35f" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-flat p-5">
        <Eyebrow className="mb-4">Median base rent trend over time</Eyebrow>
        {trendData.length < 2 ? (
          <p className="text-sm text-mute">Trend will appear once comps span at least two months.</p>
        ) : (
          <div className="h-64 w-full" data-testid="comp-trend-chart">
            <ResponsiveContainer width="100%" height={256}>
              <LineChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ebed" vertical={false} />
                <XAxis dataKey="month" tick={CHART_TICK} axisLine={{ stroke: "#e9ebed" }} tickLine={false} />
                <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`$${v}/sf`, "Median"]} contentStyle={CHART_TOOLTIP} />
                <Line type="monotone" dataKey="median_rent" stroke="#17a35f" strokeWidth={2.5} dot={{ r: 3, fill: "#17a35f", strokeWidth: 0 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <NegotiationTips />
    </div>
  );
}

const NegotiationTips = () => (
  <div className="rounded-xl border border-[#e6dfd0] bg-cream p-5" data-testid="comp-negotiation-tips">
    <p className="text-sm font-bold text-ink tracking-tight mb-3">Negotiation tips</p>
    <ul className="space-y-2 text-sm text-ink-2">
      {[
        "Anchor to the median — bring these comps to the table.",
        "Ask if rent is NNN, modified gross or gross before comparing.",
        "Negotiate free rent and TI, not just base rent.",
        "Check whether tenants at these terms survived.",
        "Get every cost in writing and have Yan review it.",
      ].map((t) => (
        <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-equal flex-shrink-0 mt-0.5" /><span>{t}</span></li>
      ))}
    </ul>
  </div>
);

/* ───────────────────────── Submit ───────────────────────── */
const EMPTY_FORM = {
  asset_class: "", zip_code: "", property_type: "", sqft_min: "", sqft_max: "",
  base_rent_psf: "", lease_type: "", lease_term: "", ti_allowance: "", free_rent: "",
  concessions: "", cannabis_zoned: false, operational_status: "unknown", deal_date: "", notes: "",
};

function SubmitTab({ meta, isAdmin, cfg, onSubmitted }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.asset_class || !form.zip_code.trim()) { toast.error("Asset class and ZIP code are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, zip_code: form.zip_code.trim(),
        sqft_min: form.sqft_min ? parseInt(form.sqft_min, 10) : null,
        sqft_max: form.sqft_max ? parseInt(form.sqft_max, 10) : null,
        base_rent_psf: form.base_rent_psf ? parseFloat(form.base_rent_psf) : null,
      };
      const res = await axios.post(`${API}/comps`, payload, cfg());
      toast.success(res.data.message);
      setForm(EMPTY_FORM);
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit comp"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="comp-submit-tab">
      <div className="rounded-xl bg-warm border border-[#f3d9b0] p-4 mb-6 text-sm text-[#7a4b0a] leading-relaxed">
        {isAdmin ? "Admin comps go live immediately." : "Reviewed by an admin before it appears. ZIP code and size range only — no names or street addresses."}
      </div>
      <div className="card-flat p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Asset class *">
            <select className={inputCls} value={form.asset_class} data-testid="comp-form-asset-class" onChange={(e) => set("asset_class", e.target.value)}>
              <option value="">Select…</option>
              {meta.asset_classes.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Property type"><input className={inputCls} placeholder="e.g. Storefront, Warehouse" value={form.property_type} data-testid="comp-form-property-type" onChange={(e) => set("property_type", e.target.value)} /></Field>
          <Field label="ZIP code *"><input className={inputCls} placeholder="90802" value={form.zip_code} data-testid="comp-form-zip" onChange={(e) => set("zip_code", e.target.value)} /></Field>
          <Field label="Base rent ($/sf per month)"><input className={inputCls} type="number" step="0.01" placeholder="3.25" value={form.base_rent_psf} data-testid="comp-form-rent" onChange={(e) => set("base_rent_psf", e.target.value)} /></Field>
          <Field label="Size — min (sq ft)"><input className={inputCls} type="number" placeholder="1000" value={form.sqft_min} data-testid="comp-form-sqft-min" onChange={(e) => set("sqft_min", e.target.value)} /></Field>
          <Field label="Size — max (sq ft)"><input className={inputCls} type="number" placeholder="2000" value={form.sqft_max} data-testid="comp-form-sqft-max" onChange={(e) => set("sqft_max", e.target.value)} /></Field>
          <Field label="Lease type">
            <select className={inputCls} value={form.lease_type} data-testid="comp-form-lease-type" onChange={(e) => set("lease_type", e.target.value)}>
              <option value="">Select…</option>
              {meta.lease_types.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Operational status">
            <select className={inputCls} value={form.operational_status} data-testid="comp-form-status" onChange={(e) => set("operational_status", e.target.value)}>
              {(meta.statuses || ["unknown"]).map((s) => <option key={s} value={s}>{meta.status_labels?.[s] || s}</option>)}
            </select>
          </Field>
          <Field label="Lease length"><input className={inputCls} placeholder="e.g. 5 years" value={form.lease_term} data-testid="comp-form-lease-term" onChange={(e) => set("lease_term", e.target.value)} /></Field>
          <Field label="TI allowance"><input className={inputCls} placeholder="e.g. $25/sf" value={form.ti_allowance} data-testid="comp-form-ti" onChange={(e) => set("ti_allowance", e.target.value)} /></Field>
          <Field label="Free rent"><input className={inputCls} placeholder="e.g. 3 months" value={form.free_rent} data-testid="comp-form-free-rent" onChange={(e) => set("free_rent", e.target.value)} /></Field>
          <Field label="Date signed"><input className={inputCls} type="date" value={form.deal_date} data-testid="comp-form-deal-date" onChange={(e) => set("deal_date", e.target.value)} /></Field>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
              <input type="checkbox" checked={form.cannabis_zoned} data-testid="comp-form-cannabis-zoned" onChange={(e) => set("cannabis_zoned", e.target.checked)} className="w-4 h-4 rounded border-line accent-[#17a35f]" />
              Cannabis-zoned site
            </label>
          </div>
        </div>
        <Field label="Concessions" className="mt-4"><input className={inputCls} placeholder="e.g. landlord-funded HVAC, reduced deposit" value={form.concessions} data-testid="comp-form-concessions" onChange={(e) => set("concessions", e.target.value)} /></Field>
        <Field label="Notes" className="mt-4"><textarea className={`${inputCls} h-24 py-2 resize-none`} placeholder="Anything else that helps applicants compare (no names or addresses)" value={form.notes} data-testid="comp-form-notes" onChange={(e) => set("notes", e.target.value)} /></Field>
        <button onClick={submit} disabled={saving} data-testid="comp-submit-button"
          className="btn-primary mt-6 h-11 px-6 disabled:opacity-60">
          {saving ? "Submitting…" : isAdmin ? "Add comparable" : "Submit for review"}
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── Review Queue (admin) ───────────────────────── */
function ReviewTab({ pending, cfg, onChanged, meta }) {
  const act = async (id, action) => {
    try {
      if (action === "approve") { await axios.patch(`${API}/comps/${id}/approve`, {}, cfg()); toast.success("Approved — now live"); }
      else { const reason = window.prompt("Reason for rejection (optional):") || ""; await axios.patch(`${API}/comps/${id}/reject`, { reason }, cfg()); toast.success("Rejected"); }
      onChanged();
    } catch { toast.error("Action failed"); }
  };

  if (pending.length === 0) return <EmptyState icon={Check} title="Nothing to review" body="Applicant-submitted comps will appear here for approval." />;

  return (
    <div className="space-y-4" data-testid="comp-review-tab">
      {pending.map((c) => (
        <div key={c.id} className="rounded-xl border border-[#f3d9b0] bg-warm p-5" data-testid={`comp-pending-${c.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-4 h-4 text-mute" />
              <span className="font-semibold text-ink">{c.asset_class}</span>
              {c.property_type && <span className="text-sm text-mute">· {c.property_type}</span>}
              <span className="text-xs font-mono text-mute ml-1">ZIP {c.zip_code}</span>
              <StatusBadge status={c.operational_status || "unknown"} labels={meta.status_labels} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => act(c.id, "approve")} data-testid={`comp-approve-${c.id}`} className="btn-primary btn-sm"><Check className="w-3.5 h-3.5" /> Approve</button>
              <button onClick={() => act(c.id, "reject")} data-testid={`comp-reject-${c.id}`} className="btn-ghost btn-sm text-[#c0392b] hover:bg-[#fdecea] hover:border-[#f2c1bb]"><X className="w-3.5 h-3.5" /> Reject</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-sm">
            <KV k="Base rent" v={c.base_rent_psf ? `${money(c.base_rent_psf)}/sf` : "—"} />
            <KV k="Size" v={sizeRange(c)} />
            <KV k="Lease type" v={c.lease_type || "—"} />
            <KV k="Lease length" v={c.lease_term || "—"} />
            <KV k="TI allowance" v={c.ti_allowance || "—"} />
            <KV k="Free rent" v={c.free_rent || "—"} />
            <KV k="Date signed" v={c.deal_date || "—"} />
            <KV k="Submitted by" v={c.submitted_by} />
          </div>
          {c.concessions && <p className="text-xs text-ink-2 mt-3"><span className="font-medium">Concessions:</span> {c.concessions}</p>}
          {c.notes && <p className="text-xs text-ink-2 mt-1"><span className="font-medium">Notes:</span> {c.notes}</p>}
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── Tutorial Modal ───────────────────────── */
const TUTORIAL_STEPS = [
  {
    title: "Welcome to Market Data",
    body: "Real, anonymized lease comps from the equity program — what others actually paid for similar space.",
    more: "Every card is a real comparable (\"comp\"). Use them as your edge in lease negotiations.",
  },
  {
    title: "Median vs. range — your anchor",
    body: "Anchor on the median; anything above the range is worth pushing back on.",
    more: "The median is the middle of the market, the range is low to high. If a landlord quotes above the range, you have data to counter.",
  },
  {
    title: "Speak the language",
    body: "Hover any underlined term for a plain-English definition.",
    more: "Especially NNN vs. gross, TI allowance, free rent and effective rent (your true cost after concessions).",
  },
  {
    title: "Did the tenant survive?",
    body: "Each comp shows whether the tenant is still operating — cheap rent that closed a business is a warning, not a target.",
    more: "Statuses: 🟢 Operating, 🔵 In build-out, 🟣 Proposed/Asking, 🔴 Closed, ⚫ Never opened, ⚪ Unknown. Proposed/asking terms are landlords' opening offers and are kept out of the benchmark medians.",
  },
  {
    title: "Compare your deal & take it with you",
    body: "Enter your proposed rent on Benchmarks to see where it lands, then export a PDF or CSV.",
    more: "Contribute your own comps too — the more applicants share, the stronger everyone's leverage.",
  },
];

function TutorialModal({ onClose }) {
  const [step, setStep] = useState(0);
  const last = step === TUTORIAL_STEPS.length - 1;
  const s = TUTORIAL_STEPS[step];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-ink/70" data-testid="comp-tutorial-modal">
      <div className="w-full max-w-2xl card-flat rounded-2xl overflow-hidden flex flex-col animate-rise-in">
        <div className="px-6 py-4 flex items-center justify-between border-b border-line">
          <div className="flex items-center gap-3">
            <EqualMark size={24} />
            <span className="text-ink font-bold tracking-tight">Market Data Guide</span>
          </div>
          <button onClick={onClose} data-testid="comp-tutorial-close" className="text-mute hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid sm:grid-cols-[200px_1fr]">
          {/* Numbered step rail */}
          <aside className="bg-page border-b sm:border-b-0 sm:border-r border-line p-3 flex sm:flex-col gap-1 overflow-x-auto">
            {TUTORIAL_STEPS.map((t, i) => (
              <div key={t.title} className={`flex items-start gap-2.5 rounded-[10px] px-2.5 py-2 flex-none sm:flex-auto ${i === step ? "bg-mint text-ink" : "text-mute"}`}>
                <span className="mt-0.5"><StepNumber n={i + 1} active={i === step} size={20} /></span>
                <span className="hidden sm:block text-xs font-medium leading-snug">{t.title}</span>
              </div>
            ))}
          </aside>
          <div className="px-7 py-7 min-h-[220px] flex flex-col">
            <Eyebrow className="text-equal-dark mb-2">Step {step + 1} of {TUTORIAL_STEPS.length}</Eyebrow>
            <h3 className="text-2xl font-bold text-ink tracking-tight mb-3">{s.title}</h3>
            <p className="text-sm text-ink-2 leading-relaxed">{s.body}</p>
            {s.more && <Learn className="mt-3" testId="comp-tutorial-more">{s.more}</Learn>}
            <div className="mt-auto pt-6 flex items-center justify-between">
              <div className="flex gap-1.5">
                {TUTORIAL_STEPS.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] ${i === step ? "w-5 bg-equal" : "w-1.5 bg-line"}`} />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} data-testid="comp-tutorial-back" className="btn-ghost h-9 px-3">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                )}
                {last ? (
                  <button onClick={onClose} data-testid="comp-tutorial-done" className="btn-primary h-9 px-5">Got it</button>
                ) : (
                  <button onClick={() => setStep(step + 1)} data-testid="comp-tutorial-next" className="btn-primary h-9 px-5">
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Small UI helpers ───────────────────────── */
const Stat = ({ label, value, accent = false }) => (
  <div className="card-flat px-4 py-3.5">
    <p className={`text-[22px] font-bold tracking-tight tabular-nums leading-tight ${accent ? "text-equal-dark" : "text-ink"}`}>{value}</p>
    <p className="text-xs text-mute mt-1">{label}</p>
  </div>
);
const Field = ({ label, children, className = "" }) => (<div className={className}><label className={labelCls}>{label}</label>{children}</div>);
const KV = ({ k, v }) => (<div><p className="th-mono text-[10px]">{k}</p><p className="text-ink truncate">{v}</p></div>);
const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="card-flat text-center py-16 px-6" data-testid="comp-empty-state">
    <div className="w-12 h-12 rounded-xl bg-page border border-line flex items-center justify-center mx-auto mb-4"><Icon className="w-6 h-6 text-mute" /></div>
    <p className="text-ink font-bold tracking-tight mb-1">{title}</p>
    <p className="text-sm text-mute max-w-sm mx-auto leading-relaxed">{body}</p>
  </div>
);
